import { useIntl } from '@weenat/wintl';
import isEmpty from 'lodash-es/isEmpty';
import isNil from 'lodash-es/isNil';
import noop from 'lodash-es/noop';
import moment from 'moment-timezone';
import { useCallback, useState } from 'react';
import { useMutation } from '..';
import { useClient } from '../ClientProvider';
import * as enums from '../enums';
import { mergeQueryState } from '../mergeState';
import { StageUsage } from '../resources/stages';
import useQuery from '../useQuery';
export const useLatestCrop = ({ plot, isEnabled = true }) => {
    const client = useClient();
    const latestCropRequest = useQuery(client.crops.getMany({ isArchived: false, plotId: plot?.id, limit: 1 }), {
        enabled: !isNil(plot?.id) && isEnabled
    });
    const [lastCrop] = !isNil(latestCropRequest.data) ? latestCropRequest.data.results : [null];
    return { lastCrop, latestCropRequest };
};
/**
 * Fetches the latest cycle from the last crops using from crops.getMany
 */
export const useLatestCycle = ({ plot, isEnabled = true }) => {
    const { lastCrop, latestCropRequest } = useLatestCrop({ isEnabled, plot });
    const hasNoActiveCrop = isNil(lastCrop);
    // Not checking for completedOn any more as we want to show a state when the cycle is complete
    const hasNoActiveCycle = isNil(lastCrop) || (!isNil(lastCrop) && isNil(lastCrop.lastCycle));
    return { hasNoActiveCrop, hasNoActiveCycle, lastCrop, latestCropRequest };
};
export const useCropTracking = ({ plot, before, isEnabled = true }) => {
    const client = useClient();
    const { t } = useIntl();
    const { lastCrop, latestCropRequest, hasNoActiveCrop, hasNoActiveCycle } = useLatestCycle({
        plot,
        isEnabled
    });
    const lastCycleId = lastCrop?.lastCycle?.id;
    const hasSomeOptions = (codes) => codes.some((code) => plot?.activeOptions.includes(code));
    const cropLastCycleStagesRequest = useQuery(client.stages.getAll({
        cycleId: lastCycleId,
        stageUsage: hasSomeOptions([enums.OptionCode.waterBalanceRI])
            ? StageUsage.bonduelleAgro
            : hasSomeOptions([enums.OptionCode.waterBalance])
                ? StageUsage.bonduelle
                : StageUsage.default
    }), {
        enabled: !isNil(lastCycleId)
    });
    const hasWaterBalance = hasSomeOptions([
        enums.OptionCode.waterBalance,
        enums.OptionCode.waterBalanceRI
    ]);
    const waterBalanceConfigRequest = useQuery(client.waterBalance.getConfig({ plotId: plot?.id }), {
        enabled: !isNil(plot?.id) && hasWaterBalance && isEnabled
    });
    const isLastCycleCompleted = !isNil(lastCrop?.lastCycle?.completedOn);
    /** We should already have a valid config when this hook is rendered */
    const waterBalanceResultRequest = useQuery(client.waterBalance.result({ cropId: lastCrop?.id, before }), {
        enabled: !isNil(lastCrop) &&
            !isNil(lastCycleId) &&
            !isLastCycleCompleted &&
            hasWaterBalance &&
            isEnabled
    });
    const { isLoading } = mergeQueryState(latestCropRequest, cropLastCycleStagesRequest, waterBalanceConfigRequest, waterBalanceResultRequest);
    const refetch = useCallback(() => {
        latestCropRequest.refetch();
        cropLastCycleStagesRequest.refetch();
        if (hasWaterBalance) {
            waterBalanceConfigRequest.refetch();
            waterBalanceResultRequest.refetch();
        }
    }, [
        latestCropRequest,
        cropLastCycleStagesRequest,
        hasWaterBalance,
        waterBalanceConfigRequest,
        waterBalanceResultRequest
    ]);
    const lastCycleStages = !isNil(cropLastCycleStagesRequest.data)
        ? cropLastCycleStagesRequest.data
        : [];
    const stages = lastCycleStages.map((x) => ({
        ...x,
        forecastedDate: waterBalanceResultRequest.data?.stages[x.bbchIndex]
    }));
    // The last stage with a date
    const currentStage = stages.find((_, i, arr) => isNil(arr[i + 1]?.date));
    // The first stage without a date
    const nextStageIndex = !isEmpty(stages) ? stages.findIndex((x) => isNil(x.date)) : null;
    const nextStage = !isNil(nextStageIndex) && nextStageIndex !== -1 ? stages[nextStageIndex] : undefined;
    const [isSkippingStages, setIsSkippingStages] = useState(false);
    const skipToStage = useCallback(async (stageId) => {
        if (!isEmpty(stages)) {
            const targetStageIndex = stages.findIndex((stg) => stg.id === stageId);
            const localNextStageIndex = nextStageIndex;
            const localNextStage = nextStage;
            const stagesToFill = [];
            const todayTs = plot
                ? moment().tz(plot?.timezone).startOf('day').unix().toString()
                : moment().startOf('day').unix().toString();
            if (!isNil(localNextStage) &&
                targetStageIndex !== -1 &&
                localNextStageIndex !== -1 &&
                !isNil(localNextStageIndex) &&
                !isNil(lastCycleId) &&
                !isNil(nextStage)) {
                setIsSkippingStages(true);
                // The fillDate method is setting a date to the next stage, making it the current stage
                // This means that when target === next we've reached the target
                for (let i = localNextStageIndex; i <= targetStageIndex; i++) {
                    stagesToFill.push(stages[i]);
                }
                const arrayOfPromises = !isEmpty(stagesToFill)
                    ? stagesToFill.map((s) => client.stages
                        .fillDate(s.id, lastCrop?.id)
                        .request({ cycleId: lastCycleId, date: todayTs }))
                    : [];
                await Promise.all(arrayOfPromises);
                refetch();
                setIsSkippingStages(false);
            }
            else {
                throw new Error("Wanted stage isn't part of the stages list.");
            }
        }
        else {
            throw new Error('Cannot compute stages on an empty array.');
        }
    }, [stages, nextStageIndex, nextStage, plot, lastCycleId, refetch, client.stages, lastCrop?.id]);
    const seedName = lastCrop?.seed?.isMaizyCompatible && lastCrop?.seed?.maizyPrecocityClass
        ? t('dss.cropTracking.status.precocity_class_label', {
            precocityClassLabel: t(`dss.maizy.legends.precocityClass.${lastCrop?.seed?.maizyPrecocityClass}`).toLowerCase()
        })
        : lastCrop?.seed?.localizedName;
    return {
        cropLastCycleStagesRequest,
        cropsRequest: latestCropRequest,
        currentStage,
        hasNoActiveCrop,
        hasNoActiveCycle,
        hasWaterBalance,
        isLoading,
        isSkippingStages,
        lastCrop,
        lastCycleId,
        nextStage,
        nextStageIndex,
        refetch,
        seedName,
        skipToStage,
        stages,
        waterBalanceConfigRequest,
        waterBalanceResultRequest
    };
};
export const useCorrectedRainfalls = ({ plotId, period, logError, isEnabled = true }) => {
    const client = useClient();
    const params = {
        plotId: plotId,
        since: period?.since,
        before: period?.before,
        span: period?.span
    };
    const areRainfallsRequestsEnabled = { enabled: !isNil(period) && !isNil(plotId) && isEnabled };
    const rainfallsRequest = useQuery(client.measurements.plots.histories.get1_1({
        metrics: ['RR'],
        ...params
    }), areRainfallsRequestsEnabled);
    const rainfallsReportsRequester = client.measurementsReports.getAllPages({
        metrics: ['RRC'],
        ...params
    });
    const rainfallsReportsRequest = useQuery(rainfallsReportsRequester, areRainfallsRequestsEnabled);
    const refetch = useCallback(() => {
        rainfallsReportsRequest.refetch();
        rainfallsRequest.refetch();
    }, [rainfallsReportsRequest, rainfallsRequest]);
    const correctedRainfalls = (rainfallsRequest.isSuccess &&
        rainfallsReportsRequest.isSuccess &&
        rainfallsRequest.data?.values.RR?.sum?.map((rainValue) => {
            const sameDayCorrectedRainsReports = rainfallsReportsRequest.data?.filter((report) => report.timestamp === `${rainValue.timestamp}`);
            if ((sameDayCorrectedRainsReports?.length || 0) > 1) {
                logError(`several Custom Rainfalls Report on the sameday for plotId: ${plotId} and timestamp: ${rainValue.timestamp}`);
            }
            const correctedRainsReport = sameDayCorrectedRainsReports?.[0] || {
                value: null,
                id: undefined,
                userId: undefined
            };
            return {
                originalValue: rainValue.value,
                timestamp: rainValue.timestamp,
                isOverride: !isNil(correctedRainsReport.value),
                value: isNil(correctedRainsReport.value) ? rainValue.value : correctedRainsReport.value,
                reportId: correctedRainsReport.id,
                userId: correctedRainsReport.userId
            };
        })) ||
        undefined;
    return {
        rainfalls: correctedRainfalls,
        ...mergeQueryState(rainfallsReportsRequest, rainfallsRequest),
        refetch,
        rainfallsReportsRequester
    };
};
export const useGoToNextStage = ({ cropId, nextStage, onSuccess = noop }) => {
    const client = useClient();
    const [fillDate, fillDateRequest] = useMutation(client.stages.fillDate(nextStage?.id, cropId), {
        onSuccess
    });
    return { fillDate, fillDateRequest };
};
export const getTranslatedStageKey = (stage, crop) => {
    const { bbchIndex } = stage;
    let leftPaddedBBCHIndex = '';
    if (!isNil(bbchIndex)) {
        if (bbchIndex.length >= 2) {
            leftPaddedBBCHIndex = bbchIndex;
        }
        else if (bbchIndex.length === 1) {
            leftPaddedBBCHIndex = `0${bbchIndex}`;
        }
    }
    return `stages_by_seed_id.seeds.${crop.seed.refWeenat}.${stage.bbchIndex === '0' ? '00' : leftPaddedBBCHIndex}`;
};
